// CPlayerStatus — AI 思考中與本家整局代打的持續狀態提示
//
// === 使用圖片 ===
// - public/assets/ui/icon-thinking.png（key: THINKING_ICON_KEY）— AI 思考中的兔女郎舉牌圖

import type Phaser from 'phaser';

import type { TSeat } from '@core/types';
import { ZH_TW } from '@i18n/zh-tw';
import { THINKING_ICON_KEY } from '@scenes/boot-scene';

import { FONT_STYLES } from './font-styles';
import { SEAT_BADGE_SPOTS } from './seat-badges';
import { UIDepth } from './ui-depths';

const THINKING_ICON_SCALE = 0.17; // 252×351 frame 顯示約 43×60 px
const THINKING_TEXT_Y = 38; // 思考文字相對圖示中心的 Y 位移
const THINKING_FRAME = 0; // 使用左側兔女郎 frame
const THINKING_DEPTH = UIDepth.PlayerStatus; // 高於風字、牌與動作提示
const THINKING_OFFSET_BY_SEAT: Record<TSeat, { x: number; y: number }> = {
  0: { x: -110, y: -90 }, // 本家：風字左上，避開手牌
  1: { x: -75, y: -70 }, // 右家：風字左上
  2: { x: -95, y: 8 }, // 對家：風字左側
  3: { x: 75, y: -70 }, // 左家：風字右上
};

const PROXY_X = 640; // 本家門前風字中心
const PROXY_Y = 495; // 本家風字上方，避免遮住手牌
const PROXY_BG_WIDTH = 190; // 代理狀態底板寬
const PROXY_BG_HEIGHT = 42; // 代理狀態底板高
const PROXY_BG_COLOR = 0x111111; // 代理狀態底色
const PROXY_BG_ALPHA = 0.78; // 代理狀態底板透明度
const PROXY_BORDER_COLOR = 0xffcf45; // 代理狀態金框
const PROXY_BORDER_WIDTH = 2; // 代理狀態框線寬

export class CPlayerStatus {
  private thinking: Phaser.GameObjects.Container | null = null;
  private readonly proxy: Phaser.GameObjects.Container;

  constructor(private readonly scene: Phaser.Scene) {
    const bg = scene.add.graphics();
    bg.fillStyle(PROXY_BG_COLOR, PROXY_BG_ALPHA);
    bg.fillRoundedRect(
      -PROXY_BG_WIDTH / 2,
      -PROXY_BG_HEIGHT / 2,
      PROXY_BG_WIDTH,
      PROXY_BG_HEIGHT,
      PROXY_BG_HEIGHT / 2
    );
    bg.lineStyle(PROXY_BORDER_WIDTH, PROXY_BORDER_COLOR, 1);
    bg.strokeRoundedRect(
      -PROXY_BG_WIDTH / 2,
      -PROXY_BG_HEIGHT / 2,
      PROXY_BG_WIDTH,
      PROXY_BG_HEIGHT,
      PROXY_BG_HEIGHT / 2
    );
    const label = scene.add
      .text(0, 0, ZH_TW.game.hint.aiProxyOn, {
        ...FONT_STYLES.body,
        color: '#ffe16a',
        fontSize: '22px',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.proxy = scene.add
      .container(PROXY_X, PROXY_Y, [bg, label])
      .setDepth(THINKING_DEPTH)
      .setVisible(false);
  }

  /** AI 開始延遲思考時顯示；同時間只會有一位決策者。 */
  showThinking(seat: TSeat): void {
    this.hideThinking();
    const spot = SEAT_BADGE_SPOTS[seat];
    const offset = THINKING_OFFSET_BY_SEAT[seat];
    const icon = this.scene.add
      .image(0, 0, THINKING_ICON_KEY, THINKING_FRAME)
      .setScale(THINKING_ICON_SCALE);
    const text = this.scene.add
      .text(0, THINKING_TEXT_Y, ZH_TW.game.hint.thinking, {
        ...FONT_STYLES.body,
        color: '#ffffff',
        fontSize: '16px',
        stroke: '#2b1208',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    this.thinking = this.scene.add
      .container(spot.x + offset.x, spot.y + offset.y, [icon, text])
      .setDepth(THINKING_DEPTH);
  }

  hideThinking(): void {
    this.thinking?.destroy(true);
    this.thinking = null;
  }

  setProxyActive(active: boolean): void {
    this.proxy.setVisible(active);
  }

  destroy(): void {
    this.hideThinking();
    this.proxy.destroy(true);
  }
}
